export type DRXErrorLevel = 'info' | 'warning' | 'error' | 'fatal';

export interface DRXErrorOptions {
  code: string;
  devMessage: string;
  userMessage?: string;
  cause?: unknown;
  context?: Record<string, unknown>;
  tags?: Record<string, string>;
  level?: DRXErrorLevel;
  fingerprint?: string[];
}

export const DEFAULT_USER_MESSAGE = 'Something went wrong. Please try again.';

export class DRXError extends Error {
  code: string;
  devMessage: string;
  userMessage: string;
  context: Record<string, unknown>;
  tags: Record<string, string>;
  level: DRXErrorLevel;
  fingerprint?: string[];

  constructor(options: DRXErrorOptions) {
    super(options.devMessage);
    this.name = 'DRXError';
    this.code = options.code;
    this.devMessage = options.devMessage;
    this.userMessage = options.userMessage || DEFAULT_USER_MESSAGE;
    this.context = options.context || {};
    this.tags = options.tags || {};
    this.level = options.level || 'error';
    this.fingerprint = options.fingerprint;

    if (options.cause !== undefined) {
      (this as Error & { cause?: unknown }).cause = options.cause;
    }
  }
}

export const isDRXError = (error: unknown): error is DRXError =>
  error instanceof DRXError;

const safeStringify = (value: unknown): string => {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

export const toDRXError = (
  error: unknown,
  defaults: Partial<DRXErrorOptions> = {}
): DRXError => {
  if (isDRXError(error)) {
    return error;
  }

  if (error instanceof Error) {
    return new DRXError({
      code: defaults.code || 'UNEXPECTED_ERROR',
      devMessage: defaults.devMessage || error.message || 'Unexpected error',
      userMessage: defaults.userMessage,
      cause: error,
      context: defaults.context,
      tags: defaults.tags,
      level: defaults.level,
      fingerprint: defaults.fingerprint,
    });
  }

  if (typeof error === 'string') {
    return new DRXError({
      code: defaults.code || 'UNEXPECTED_ERROR',
      devMessage: defaults.devMessage || error,
      userMessage: defaults.userMessage,
      cause: error,
      context: defaults.context,
      tags: defaults.tags,
      level: defaults.level,
      fingerprint: defaults.fingerprint,
    });
  }

  return new DRXError({
    code: defaults.code || 'UNEXPECTED_ERROR',
    devMessage:
      defaults.devMessage ||
      `Unexpected non-error rejection: ${safeStringify(error)}`,
    userMessage: defaults.userMessage,
    cause: error,
    context: defaults.context,
    tags: defaults.tags,
    level: defaults.level,
    fingerprint: defaults.fingerprint,
  });
};

